/**
 * 設定 → 帳票番号（#268 1.5b、docs/98 §6、../eigyouryoku/docs/12 §3 決めごと10）。iOS 風（docs/10 §9.15）。Figma は無し（依頼文「今の設定の型のまま」）。
 *   /settings/numbering             種類ごとの行（見積書・請求書・領収書・{deliveryNote}・{acceptance}・{order}・{paymentNotice}・見積依頼書。門と使う機能で出し分け）。右は「次 I-0013」
 *   /settings/numbering/:docType    書式（記号のボタン 年4桁・年2桁・月・連番＝カーソルの位置に入る）・見本・連番リセット・次の番号・手入力（請求書）・最後に発行した番号。
 *     番号に効く設定なので、変えただけでは保存しない（自動ログアウトと同じく「取消」「決定」＝設定の画面の自動保存の例外）。決定は管理者だけ
 */
import {
  MANUAL_NUMBER_DOC_TYPES,
  NUMBERING_DOC_LABEL,
  NUMBERING_MSG as M,
  NUMBER_RESET_LABEL,
  NUMBER_TOKENS,
  NumberFormat,
  NumberingDocType,
  NumberReset,
  formatDocumentNo,
  type NumberingItem,
  type NumberingList,
  type NumberingSave,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupInputRow, GroupList, GroupNavRow, GroupNumberRow, GroupSection, GroupSelectRow, GroupSwitchRow, GroupValueRow } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { useUses } from "../core/uses.js";
import { t } from "../labels.js";
import { todayJst } from "../lib/num.js";

export const NUMBERING_KEY = ["company-numbering"] as const;

/** 門と使う機能で出す種類 */
function useShown(): (d: NumberingDocType) => boolean {
  const me = useMe();
  const can = useUses();
  const sales = !!me.data?.tenant.modules.includes("sales");
  return (d) =>
    d === "quote" || d === "invoice" || d === "receipt" ? true
    : d === "delivery" ? sales && can("deliveryNote")
    : d === "acceptance" ? sales && can("acceptance")
    : d === "order" || d === "payment_notice" ? sales
    : d === "rfq" ? can("rfq")
    : false;
}

export function SettingsNumberingPage() {
  const q = useQuery({ queryKey: NUMBERING_KEY, queryFn: () => api<NumberingList>("GET", "/company/numbering") });
  const back = useBackNav({ to: "/settings", label: "設定" }, false);
  usePageName(M.title);
  const cls = useRestPageClass("numberingpage");
  const shown = useShown();
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  return (
    <div className={cls} data-testid="numbering-settings">
      <SettingsHead back={back} title={M.title} />
      <GroupSection help={<Help testId="help-numbering" label={M.title} text={M.help} />} data-testid="numbering-sec">
        <GroupList>
          {q.data.items
            .filter((x) => shown(x.docType))
            .map((x) => (
              <GroupNavRow key={x.docType} title={t(NUMBERING_DOC_LABEL[x.docType])} value={M.nextValue(x.sample)} valueTestId={`numbering-value-${x.docType}`} to={`/settings/numbering/${x.docType}`} buttonId={`toNumbering-${x.docType}`} testId={`numbering-row-${x.docType}`} />
            ))}
        </GroupList>
      </GroupSection>
    </div>
  );
}

export function SettingsNumberingEditPage() {
  const { docType: raw = "" } = useParams();
  const d = NumberingDocType.safeParse(raw);
  const q = useQuery({ queryKey: NUMBERING_KEY, queryFn: () => api<NumberingList>("GET", "/company/numbering") });
  if (!d.success) return <Navigate to="/settings/numbering" replace />;
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  const item = q.data?.items.find((x) => x.docType === d.data);
  if (!q.data || !item) return <p className="hint">読み込んでいます…</p>;
  return <NumberingEditor key={`${item.docType}|${item.format}|${item.next}|${item.reset}|${item.allowManual}`} item={item} fiscal={q.data.fiscalStartMonth} />;
}

type Errs = { format?: string | undefined; next?: string | undefined; other?: string | undefined };

function NumberingEditor({ item, fiscal }: { item: NumberingItem; fiscal: number }) {
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const { toast, node } = useToast();
  const label = t(NUMBERING_DOC_LABEL[item.docType]);
  const [format, setFormat] = useState(item.format);
  const [reset, setReset] = useState<NumberReset>(item.reset);
  const [next, setNext] = useState<number | null>(item.next);
  const [manual, setManual] = useState(item.allowManual);
  const [err, setErr] = useState<Errs>({});
  const fmtRef = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const changed = format !== item.format || reset !== item.reset || manual !== item.allowManual || next !== item.next;
  const back = useBackNav({ to: "/settings/numbering", label: M.title }, changed);
  usePageName(label);
  const cls = useRestPageClass("numberingpage");
  const fmtOk = NumberFormat.safeParse(format);
  const sample = fmtOk.success && next != null && next >= 1 ? formatDocumentNo(fmtOk.data, next, todayJst(), fiscal) : M.none;

  /** 記号をカーソルの位置に（欄を触っていなければ末尾） */
  const putToken = (token: string) => {
    const el = fmtRef.current;
    const a = el?.selectionStart ?? format.length;
    const b = el?.selectionEnd ?? format.length;
    const v = format.slice(0, a) + token + format.slice(b);
    caret.current = a + token.length;
    setFormat(v);
    setErr({});
  };
  // 記号を入れたあとのカーソル：描いた直後に（次の入力より先に）入れた記号の後ろへ
  useLayoutEffect(() => {
    const el = fmtRef.current;
    if (caret.current == null || !el) return;
    el.focus();
    el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [format]);
  const restore = () => {
    setFormat(item.format);
    setReset(item.reset);
    setNext(item.next);
    setManual(item.allowManual);
    setErr({});
  };

  const save = useMutation({
    mutationFn: () =>
      api<NumberingList>("PUT", `/company/numbering/${item.docType}`, {
        format,
        reset,
        allowManual: manual,
        // 次の番号は変えたとき・書式か周期を変えたとき（新しい期間・書式のカウンタ）だけ送る
        ...(next !== item.next || format !== item.format || reset !== item.reset ? { next } : {}),
      } satisfies NumberingSave),
    onSuccess: (v) => {
      qc.setQueryData(NUMBERING_KEY, v);
      setErr({});
      toast(M.saved(label), undefined, "success", "numbering-saved");
    },
    onError: (e) => {
      const f = e instanceof ApiError ? e.problem.errors : undefined;
      const fe = typeof f?.format === "string" ? f.format : undefined;
      const ne = typeof f?.next === "string" ? f.next : undefined;
      setErr({ format: fe, next: ne, other: fe || ne ? undefined : messageOf(e) });
    },
  });
  const onSave = () => {
    if (!fmtOk.success) return setErr({ format: fmtOk.error.issues[0]?.message ?? M.formatNoSeq });
    if (next == null || next < 1) return setErr({ next: M.nextTooSmall(1) });
    if (next < item.minNext && (next !== item.next || format !== item.format || reset !== item.reset)) return setErr({ next: M.nextTooSmall(item.minNext) });
    save.mutate();
  };

  return (
    <div className={cls} data-testid="numbering-edit">
      <SettingsHead back={back} title={label} titleTestId="numbering-title" />
      <GroupSection help={<Help testId="help-numbering-edit" label={M.title} text={`${M.help}${MANUAL_NUMBER_DOC_TYPES.includes(item.docType) ? `。${M.allowManualHelp}` : ""}`} />} data-testid="numbering-edit-sec">
        <GroupList>
          <GroupInputRow id="nb_format" label={M.format} wide error={err.format}>
            <input id="nb_format" ref={fmtRef} className="giinp num" value={format} maxLength={40} readOnly={!admin} autoComplete="off" spellCheck={false} aria-invalid={err.format ? true : undefined} aria-describedby={err.format ? "nb_format-err" : undefined} onChange={(e) => (setFormat(e.target.value), setErr({}))} data-testid="numbering-format" />
          </GroupInputRow>
          {admin ? (
            <li className="grow grow-set nb-tokens" data-testid="numbering-tokens">
              <div className="growin">
                <span className="gtx">
                  <span className="gtt">{M.tokens}</span>
                </span>
                <span className="nb-tokbtns">
                  {NUMBER_TOKENS.map((x) => (
                    <button key={x.token} type="button" className="btn tonal sm" onMouseDown={(e) => e.preventDefault()} onClick={() => putToken(x.token)} data-testid={`numbering-token-${x.label}`}>
                      {x.label}
                    </button>
                  ))}
                </span>
              </div>
            </li>
          ) : null}
          <GroupValueRow title={M.sample} value={sample} valueTestId="numbering-sample" className="nb-num" />
          <GroupSelectRow
            id="nb_reset"
            label={M.reset}
            value={reset}
            options={NumberReset.options.map((r) => ({ value: r, label: NUMBER_RESET_LABEL[r] }))}
            disabled={!admin}
            onChange={(v) => (setReset(NumberReset.parse(v)), setErr({}))}
            selectTestId="numbering-reset"
          />
          <GroupNumberRow id="nb_next" label={M.next} kind="int" value={next} unit={null} disabled={!admin} error={err.next} onChange={(v) => (setNext(v), setErr({}))} inputTestId="numbering-next" />
          {MANUAL_NUMBER_DOC_TYPES.includes(item.docType) ? (
            <GroupSwitchRow label={M.allowManual} name={M.allowManual} checked={manual} disabled={!admin} onChange={(v) => (setManual(v), setErr({}))} testId="numbering-manual" rowTestId="row-numbering-manual" />
          ) : null}
          <GroupValueRow title={M.lastNo} value={item.lastNo ?? M.none} valueTestId="numbering-last" className="nb-num" />
        </GroupList>
      </GroupSection>
      <ErrorText>{err.other}</ErrorText>
      {admin ? (
        <div className="autologout-acts">
          <button type="button" className="btn" disabled={!changed || save.isPending} onClick={restore} data-testid="numbering-cancel">
            {M.cancel}
          </button>
          <button type="button" className="btn primary" disabled={!changed || save.isPending} onClick={onSave} data-testid="numbering-confirm">
            {M.confirm}
          </button>
        </div>
      ) : null}
      {node}
    </div>
  );
}
